/**
 * Residential fee policy pinned for calculator version RAC_DAY_CALC_V3.
 *
 * These are Paul's recommended defaults (WS3 §4 and ENGINEERING-PLAN §4 items
 * 1 and 9). A later version can change them without rewriting history: the
 * version string is stored on every day result.
 *
 * - Departure day: charge BDF and accommodation. Do not charge means-tested
 *   components (hotelling, NCCC, MTCF, ITF) unless the caller sets CHARGED.
 * - Means pending (no DMTA, and means are not "not disclosed"): charge BDF
 *   and accommodation. Means components are zero and retro-billable.
 * - Blank `providerInformedOn` uses `sourceIssuedAt`, with an assumed reason.
 * - Fee-reduction remainder after BDF, the means component, and accommodation
 *   is discarded and reported. It is not carried to the next day.
 * - Hardship order is BDF → hotelling → NCCC → accommodation, or for
 *   grandfathered MTCF: BDF → MTCF → accommodation. Never below zero. Never
 *   optional services, HELF, or the bed reservation fee.
 */

export const RAC_DAY_CALC_VERSION = 'RAC_DAY_CALC_V3' as const;

export type ResidentialDepartureMeansPolicy = 'NOT_CHARGED' | 'CHARGED';

export type ResidentialMeansPendingPolicy = 'ZERO_RETRO_BILLABLE';

export interface ResidentialFeePolicyV3 {
  version: typeof RAC_DAY_CALC_VERSION;
  departureDayMeansTested: ResidentialDepartureMeansPolicy;
  meansPending: ResidentialMeansPendingPolicy;
  /** D8. When providerInformedOn is blank, use sourceIssuedAt. */
  assumeProviderInformedFromSourceIssuedAt: boolean;
  /**
   * Billing sets this true. A means-tested care day with no first-entry day
   * then holds. Existing vectors omit it and stay billable.
   */
  requireMtcfFirstEntryDay?: boolean;
}

export const DEFAULT_RESIDENTIAL_FEE_POLICY_V3: ResidentialFeePolicyV3 = {
  version: RAC_DAY_CALC_VERSION,
  departureDayMeansTested: 'NOT_CHARGED',
  meansPending: 'ZERO_RETRO_BILLABLE',
  assumeProviderInformedFromSourceIssuedAt: true,
};

export function resolveResidentialFeePolicy(
  policy?: Partial<ResidentialFeePolicyV3> | null,
): ResidentialFeePolicyV3 {
  return {
    departureDayMeansTested:
      policy?.departureDayMeansTested ?? DEFAULT_RESIDENTIAL_FEE_POLICY_V3.departureDayMeansTested,
    meansPending: policy?.meansPending ?? DEFAULT_RESIDENTIAL_FEE_POLICY_V3.meansPending,
    assumeProviderInformedFromSourceIssuedAt:
      policy?.assumeProviderInformedFromSourceIssuedAt ??
      DEFAULT_RESIDENTIAL_FEE_POLICY_V3.assumeProviderInformedFromSourceIssuedAt,
    requireMtcfFirstEntryDay: policy?.requireMtcfFirstEntryDay ?? false,
    version: RAC_DAY_CALC_VERSION,
  };
}
